import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import { callModeOptions, minutesToSeconds, secondsToMinutes } from '../../../utils/common';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import { userAdd, userUpdate } from '../../../store/slices/tenantSlices/configurationSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import FormNumberInput from '../../../components/form/FormNumberInput';

const AddEditUserModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null,
  setOpenProcessAddModal
}) => {
  const dispatch = useDispatch();
  const [processOptions, setProcessOptions] = useState([]);

  const initialValues = {
    Username: '',
    Password: '',
    email: '',
    agent_name: '',
    status: 'true',
    process: '',
    callmode: '',
    manualcall: 'true',
    CDRs: 'true',
    autocallback: 'false',
    chat_with_whatsapp: 'false',
    is_redirect_to_whatsapp: 'false',
    internaltransfer: 'true',
    product_manage: 1,
    max_user_limit: 0,
    web_phone: 0,
    forwarding_pstn: null,
    is_auto: 'disable',
    multiple_process: [],
    is_multiple_process: 'no'
  };

  const validationSchema = Yup.object({
    Username: Yup.string().required('Username is required'),
    Password: Yup.string().required('Password is required'),
    process: Yup.string().required('process is required'),
    callmode: Yup.string().required('call mode is required')
  });

  const buildPayload = (values, editRecord, isEdit) => {
    const payload = { ...values };

    payload.forwarding_pstn =
      values?.forwarding_pstn && values.forwarding_pstn > 0 ? values.forwarding_pstn : null;

    payload.multiple_process = payload.is_multiple_process === 'yes' ? values.multiple_process : [];

    payload.is_redirect_to_whatsapp = values.is_redirect_to_whatsapp === 'true';

    if (isEdit) {
      payload.id = editRecord.id;
      payload.max_user_limit = minutesToSeconds(values?.max_user_limit);
    }

    return payload;
  };

  const onSubmit = async () => {
    try {
      const payload = buildPayload(values, editRecord, isEdit);

      const action = isEdit ? userUpdate : userAdd;
      const successMessage = isEdit ? 'Record Updated Successfully' : 'Record Added Successfully';

      const response = await dispatch(action(payload)).unwrap();

      if (response?.message) {
        toast.success(successMessage);
        onCloseClick(true);
        resetForm();
      } else {
        toast.error(response?.error || 'Something went wrong');
      }
    } catch (error) {
      console.error('onSubmit error:', error);
      toast.error('Failed to save record');
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
  }, []);

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...values,
        Username: editRecord?.Username,
        Password: editRecord?.Password,
        email: editRecord?.email,
        agent_name: editRecord?.agent_name || '',
        status: String(editRecord?.status),
        process: Number(editRecord?.process),
        callmode: editRecord?.callmode,
        manualcall: String(editRecord?.manualcall),
        CDRs: String(editRecord?.CDRs),
        // autocallback: String(editRecord?.autocallback),
        internaltransfer: String(editRecord?.internaltransfer),
        product_manage: Number(editRecord?.product_manage),
        web_phone: Number(editRecord?.web_phone),
        forwarding_pstn: Number(editRecord?.forwarding_pstn) || null,
        max_user_limit: secondsToMinutes(Number(editRecord?.max_user_limit)) || 0,
        chat_with_whatsapp: String(editRecord?.chat_with_whatsapp) || 'false',
        is_redirect_to_whatsapp: String(editRecord?.is_redirect_to_whatsapp ?? false),
        is_auto: String(editRecord?.is_auto) || 'disable',
        is_multiple_process: String(editRecord?.is_multiple_process) || 'no',
        multiple_process: editRecord?.multi_process || []
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit User' : 'Add User'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="Username"
                label="Name"
                placeholder="Enter Name"
                value={values.Username}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="Password"
                label="Password"
                placeholder="Enter Password"
                value={values.Password}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="email"
                label="Email"
                type="email"
                placeholder="Enter Email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="agent_name"
                label="Agent Name"
                placeholder="Enter Agent Name"
                value={values.agent_name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process"
                label="Process"
                placeholder="Select Process"
                value={values.process}
                options={processOptions}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const oldValue = values.process;

                  handleChange(e);

                  if (values.is_multiple_process === 'yes') {
                    let updatedProcesses = [...(values.multiple_process || [])];

                    if (oldValue && oldValue !== newValue) {
                      updatedProcesses = updatedProcesses.filter((item) => item !== oldValue);
                    }

                    if (!updatedProcesses.includes(newValue)) {
                      updatedProcesses.push(newValue);
                    }

                    setFieldValue('multiple_process', updatedProcesses);
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
                createName="Add Process"
                isAdd={true}
                onCreateClick={() => {
                  setOpenProcessAddModal(true);
                  onCloseClick();
                }}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="callmode"
                label="Call Mode"
                placeholder="Select Call Mode"
                value={values.callmode}
                options={callModeOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="max_user_limit"
                label="Max user limit (Min)"
                placeholder="Enter max user limit"
                value={values.max_user_limit}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, '');
                  const numericValue = rawValue === '' ? '' : parseInt(rawValue, 10);
                  setFieldValue('max_user_limit', numericValue);
                }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === '.') {
                    e.preventDefault();
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                pattern="^[0-9]*$"
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="forwarding_pstn"
                label="Forwarding PSTN"
                placeholder="Enter Forwarding PSTN"
                value={values.forwarding_pstn}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_multiple_process"
                label="Use Multiple Process"
                value={String(values.is_multiple_process === 'yes')}
                onChange={() => {
                  setFieldValue(
                    'is_multiple_process',
                    values.is_multiple_process === 'yes' ? 'no' : 'yes'
                  );
                  setFieldValue(
                    'multiple_process',
                    values.process && values.is_multiple_process === 'no' ? [values.process] : []
                  );
                }}
              />
            </Col>
            {values.is_multiple_process === 'yes' ? (
              <Col lg="3">
                <FormSelect
                  name="multiple_process"
                  label="Multiple Process"
                  placeholder="Select Process"
                  value={values.multiple_process}
                  options={processOptions}
                  onChange={(e) => {
                    if (![...(e.target.value || [])]?.includes(values.process)) {
                      setFieldValue('multiple_process', [...e.target.value, values.process]);
                      return;
                    }
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  menuPlacement="bottom"
                  createName="Add Process"
                  isAdd={true}
                  onCreateClick={() => {
                    setOpenProcessAddModal(true);
                    onCloseClick();
                  }}
                  isMulti={true}
                />
              </Col>
            ) : null}
            <Col lg="3">
              <FormToggle
                name="manualcall"
                label="Manual Call"
                value={values.manualcall}
                onChange={(e) => setFieldValue('manualcall', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="CDRs"
                label="CDRs"
                value={values.CDRs}
                onChange={(e) => setFieldValue('CDRs', e)}
              />
            </Col>
            {/* <Col lg="3">
              <FormToggle
                name="autocallback"
                label="Auto Callback"
                value={values.autocallback}
                onChange={(e) => setFieldValue('autocallback', e)}
              />
            </Col> */}
            <Col lg="3">
              <FormToggle
                name="internaltransfer"
                label="Internal transfer"
                value={values.internaltransfer}
                onChange={(e) => setFieldValue('internaltransfer', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="product_manage"
                trueName="Yes"
                falseName="No"
                label="Product Manage"
                value={String(values.product_manage === 0)}
                onChange={(e) =>
                  setFieldValue('product_manage', values.product_manage === 0 ? 1 : 0)
                }
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="web_phone"
                trueName="Yes"
                falseName="No"
                label="Web Phone"
                value={String(values.web_phone === 0)}
                onChange={(e) => setFieldValue('web_phone', values.web_phone === 0 ? 1 : 0)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="chat_with_whatsapp"
                label="Chat with whatsapp"
                value={values.chat_with_whatsapp}
                onChange={(e) => setFieldValue('chat_with_whatsapp', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_redirect_to_whatsapp"
                label="Redirect to Whatsapp"
                value={values.is_redirect_to_whatsapp}
                onChange={(e) => setFieldValue('is_redirect_to_whatsapp', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_auto"
                trueName="Yes"
                falseName="No"
                label="Auto Follow Up"
                value={String(values.is_auto === 'enable')}
                onChange={(e) =>
                  setFieldValue('is_auto', values.is_auto === 'enable' ? 'disable' : 'enable')
                }
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditUserModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any,
  setOpenProcessAddModal: PropTypes.func
};

export default AddEditUserModal;
